import React from 'react'
import { motion } from 'framer-motion'
import { Users, Clock, BarChart3, Layout } from 'lucide-react'

const features = [
  {
    icon: <Users className='w-5 h-5 text-primary' />,
    title: 'Seamless Collaboration',
    description:
      'Work together effortlessly, share tasks, and update progress in real-time.',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/20',
  },
  {
    icon: <Clock className='w-5 h-5 text-indigo-400' />,
    title: 'Time Management',
    description:
      'Optimize your time with integrated timers, reminders, and smart schedules.',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/20',
  },
  {
    icon: <BarChart3 className='w-5 h-5 text-amber-400' />,
    title: 'Advanced Tracking',
    description:
      "A bird's eye view of your entire productivity and team behavior.",
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
  {
    icon: <Layout className='w-5 h-5 text-emerald-400' />,
    title: 'Custom Workspaces',
    description:
      'Structure your workflow exactly how you want it with flexible views.',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 100, damping: 15 },
  },
} as const

const Features: React.FC = () => {
  return (
    <section
      id='features'
      className='py-24 bg-[#0a0a0a] relative overflow-hidden'
    >
      {/* Decorative background lines */}
      <div className='absolute inset-0 pointer-events-none opacity-50'>
        <div className='absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#1f1f1f] to-transparent'></div>
        <div className='absolute top-0 left-1/2 w-[1px] h-full bg-gradient-to-b from-transparent via-[#1f1f1f] to-transparent'></div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10'>
        <div className='text-center max-w-2xl mx-auto mb-16'>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className='inline-flex items-center px-3 py-1 rounded-full border border-[#262626] bg-[#1a1a1a] text-xs font-semibold text-slate-400 tracking-wide uppercase mb-4'
          >
            Features
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className='text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight'
          >
            Keep everything in one place
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className='text-lg md:text-xl text-slate-400 leading-relaxed'
          >
            Forget complex project management tools. Experience the elegant
            simplicity of intelligent task orchestration.
          </motion.p>
        </div>

        <motion.div
          variants={containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, margin: '-100px' }}
          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className='group bg-[#1a1a1a] rounded-2xl p-6 border border-[#262626] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.5)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.8)] transition-all duration-300 transform hover:-translate-y-1'
            >
              <div
                className={`w-12 h-12 rounded-xl rounded-tl-sm flex items-center justify-center mb-6 bg-gradient-to-br ${feature.bgColor} ${feature.borderColor} border`}
              >
                {feature.icon}
              </div>
              <h3 className='text-xl font-bold text-white mb-3 group-hover:text-primary transition-colors'>
                {feature.title}
              </h3>
              <p className='text-slate-400 leading-relaxed text-sm'>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Features
